import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { jwtDecode } from "jwt-decode";
import axiosInstance from "../helpers/axiosInstance";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Swal from "sweetalert2";
import './profile.css';

const Profile = () => {
  const [user, setUser] = useState({});
  const [profileImage, setProfileImage] = useState("/default-profile.png");
  const [editedUser, setEditedUser] = useState({ name: "", mobile: "" });
  const [userRole, setUserRole] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");


  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const toggleOldPasswordVisibility = () => {
    setShowOldPassword(!showOldPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    if (token) {
      try {
        const decoded = jwtDecode(token);

        const fetchUserProfile = async () => {
          try {
            const response = await axiosInstance.get(`/api/auth/user/image/${decoded.userId}`);
            const { profile_image } = response.data.user;

            if (profile_image) {
              setProfileImage(`http://localhost:4000/uploads/${profile_image}`);
            } else {
              setProfileImage("");
            }
          } catch (err) {
            console.error("Failed to fetch user profile:", err);
          }
        };

        fetchUserProfile();
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    if (token) {
      try {
        const decoded = jwtDecode(token);
        const userId = decoded.userId;
        const role = decoded.role;

        setUserRole(role);

        axiosInstance.get(`/api/auth/user/${userId}`)
          .then((res) => {
            setUser(res.data.user);
            setEditedUser({
              name: res.data.name || "",
              mobile: res.data.mobile_number || ""
            });
          })
          .catch((err) => {
            console.error("Failed to fetch user details:", err);
          });

      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
  }, []);


  const handleImageChangeAndSave = async (event) => {
    const file = event.target.files[0];
    if (!file || !user?.id) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await axiosInstance.put(
        `/api/auth/user/${user.id}/profile-image`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const { image } = response.data;
      if (image) {
        const imageUrl = `${process.env.REACT_APP_API_BASE_URL}uploads/${image}`;
        setProfileImage(imageUrl);
        Swal.fire({
          icon: "success",
          title: "Profile Image Updated",
          text: "Your profile picture has been successfully updated!",
          confirmButtonText: "OK"
        });
      }
    } catch (error) {
      console.error("Image upload failed:", error);

      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "There was a problem uploading your image.";

      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: backendMessage,
        confirmButtonText: "OK"
      });
    }

  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const decoded = jwtDecode(token);
      const userId = decoded.userId;

      const updatedData = {
        name: editedUser.name,
        mobile_number: editedUser.mobile,
      };

      const res = await axiosInstance.put(`/api/auth/update/${userId}`, updatedData);

      setUser(res.data.user);
      Swal.fire({
        icon: "success",
        title: "Profile Updated",
        text: res.data.message || "Your profile information has been successfully saved!",
        confirmButtonText: "OK"
      })

    } catch (err) {
      const backendMessage = err?.response?.data?.message;

      Swal.fire({
        icon: backendMessage ? "success" : "error",
        title: backendMessage ? "Update Successful" : "Update Failed",
        text: backendMessage || "Something went wrong while saving your changes.",
        confirmButtonText: "OK"
      });

      console.error("Update error:", err);
    }
  };


  const handleLogout = async () => {
    try {
      const response = await axiosInstance.post("api/auth/logout");
      if (!response.ok) {
        console.warn("Server logout failed:", await response.text());
      }
    } catch (err) {
      console.error("Logout request failed:", err);
    } finally {
      // Clear all client-side tokens & data
      localStorage.clear();
      sessionStorage.clear();

      // Navigate to login page
      window.location.href = "/login";

      // Reload only if absolutely necessary (e.g., resetting app state)
      // Avoid reload unless your app depends on it
      // window.location.reload();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");
    const decoded = jwtDecode(token);
    const userId = decoded.userId;

    if (newPassword !== confirmPassword) {
      handleCloseModal();
      Swal.fire({
        icon: "error",
        title: "Passwords do not match",
        text: "Please ensure both password fields match.",
        confirmButtonText: "OK"
      });
      return;
    }

    try {
      await axiosInstance.put(`/api/auth/change-password/${userId}`, {
        oldPassword: user.password,
        newPassword: newPassword
      });

      handleCloseModal();
      Swal.fire({
        icon: "success",
        title: "Password Updated",
        text: "Your password has been successfully updated!",
        confirmButtonText: "OK"
      });

    } catch (error) {
      console.error("Failed to update password:", error);
      handleCloseModal();
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || "There was a problem updating your password.",
        confirmButtonText: "OK"
      });
    }
  }

  const handleCloseModal = () => {
    setUser({ ...user, password: "" });
    setNewPassword("");
    setConfirmPassword("");
  };


  return (
    <div className="container-fluid py-5" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <div className="responsive-scroll">
        <div className="row shadow-sm overflow-hidden" style={{ maxWidth: "1000px", margin: "0 auto" }}>
          {/* Left Panel - Details & Actions */}
          <div className="col-12 col-md-5 d-flex flex-column justify-content-between p-4" style={{ backgroundColor: "#f9f9f9" }}>
            <div>
              <h5 className="fw-bold mt-2 mb-4">Profile Info</h5>
              <div className="d-flex flex-column align-items-center text-center mb-5">
                <div className="p-3 text-center">
                  <label htmlFor="profilePicInput" className="position-relative d-inline-block">
                    <div
                      className="rounded-circle border border-3 border-secondary shadow-sm"
                      style={{
                        width: "130px",
                        height: "130px",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <img
                        src={profileImage || "https://via.placeholder.com/130"}
                        alt="Profile"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          cursor: "pointer"
                        }}
                      />
                    </div>

                    <div
                      className="position-absolute"
                      style={{
                        bottom: "5px",
                        right: "5px",
                        background: "rgba(0, 0, 0, 0.7)",
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <i className="bi bi-pencil text-white" style={{ fontSize: "16px", cursor: "pointer" }}></i>
                    </div>
                  </label>

                  <input
                    type="file"
                    id="profilePicInput"
                    className="d-none"
                    accept="image/*"
                    onChange={handleImageChangeAndSave}
                  />
                </div>
                <h5 className="mb-1 text-center">{user.name}</h5>
                <p className="text-muted small mb-0 text-center">{user.email}</p>
              </div>
            </div>

            <div className="d-flex flex-column mt-auto gap-1 align-items-center align-items-md-start">
              <button
                className="btn border-0 text-start"
                style={{
                  fontSize: "15px",
                  width: "200px",
                  backgroundColor: activeTab === "profile" ? "#d3d3d3" : "transparent",
                  color: activeTab === "profile" ? "#000" : "#0d6efd"
                }}
                onClick={() => setActiveTab("profile")}
              >
                <i className="bi bi-person-circle me-1"></i> My Profile
              </button>

              <button
                className="btn border-0 text-start"
                style={{
                  fontSize: "15px",
                  width: "200px",
                  backgroundColor: activeTab === "settings" ? "#d3d3d3" : "transparent",
                  color: activeTab === "settings" ? "#000" : "#0d6efd"
                }}
                onClick={() => setActiveTab("settings")}
              >
                <i className="bi bi-gear me-1"></i> Settings
              </button>

              <button
                className="btn border-0 text-start"
                style={{
                  fontSize: "15px",
                  width: "200px",
                  backgroundColor: activeTab === "password" ? "#d3d3d3" : "transparent",
                  color: activeTab === "password" ? "#000" : "#0d6efd"
                }}
                onClick={() => setActiveTab("password")}
              >
                <i className="bi bi-key me-1"></i> Change Password
              </button>

              <button
                onClick={handleLogout}
                className="btn btn-outline-danger border-0 text-start d-flex align-items-center"
                style={{
                  fontSize: "15px",
                  width: "200px"
                }}
              >
                <i className="bi bi-box-arrow-right me-1"></i> Logout
              </button>
            </div>
          </div>


          <div className="col-md-7 col-12 bg-white p-4 p-md-5">
            <div className="row">
              <div className="col-12">
                {activeTab === "profile" && (
                  <>
                    <div className="d-flex flex-column flex-md-row align-items-center mb-4 gap-3">
                      <img
                        src={profileImage || "https://via.placeholder.com/140"}
                        alt="Profile"
                        className="rounded-circle shadow"
                        style={{ width: "50px", height: "50px", objectFit: "cover" }}
                      />
                      <div className="text-center text-md-start">
                        <h6 className="mb-0">{user.name}</h6>
                        <p className="text-muted small mb-0">{user.email}</p>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="text-muted small">Name</label>
                      <div className="p-2 bg-white rounded border">{user.name}</div>
                    </div>

                    <div className="mb-3">
                      <label className="text-muted small">Email</label>
                      <div className="p-2 bg-white rounded border">{user.email}</div>
                    </div>

                    <div className="mb-3">
                      <label className="text-muted small">Mobile</label>
                      <div className="p-2 bg-white rounded border">{user.mobile_number}</div>
                    </div>

                    <div className="mb-3">
                      <label className="text-muted small">Role</label>
                      <div className="p-2 bg-white rounded border">{userRole}</div>
                    </div>
                  </>
                )}

                {activeTab === "password" && (
                  <>
                    <h5 className="fw-bold mb-4">Change Your Password</h5>
                    <p className="text-start mb-4" style={{ fontSize: "14px", color: "#666" }}>
                      Make sure your new password is strong and easy for you to remember.
                    </p>

                    <form onSubmit={handleChangePassword}>
                      <div className="mb-3 position-relative">
                        <label className="form-label" style={{ fontSize: "14px" }}>
                          Old Password <span style={{ color: "red" }}>*</span>
                        </label>
                        <input
                          type={showOldPassword ? "text" : "password"}
                          className="form-control"
                          value={user.password}
                          onChange={(e) => setUser({ ...user, password: e.target.value })}
                        />
                        <span
                          className="position-absolute end-0 translate-middle-y me-3"
                          onClick={toggleOldPasswordVisibility}
                          style={{ cursor: "pointer", opacity: "0.5", top: "45px" }}
                        >
                          {showOldPassword ? <FaEye /> : <FaEyeSlash />}
                        </span>
                      </div>

                      <div className="mb-3 position-relative">
                        <label className="form-label" style={{ fontSize: "14px" }}>
                          New Password <span style={{ color: "red" }}>*</span>
                        </label>
                        <input
                          type={showNewPassword ? "text" : "password"}
                          className="form-control"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <span
                          className="position-absolute end-0 translate-middle-y me-3"
                          onClick={toggleNewPasswordVisibility}
                          style={{ cursor: "pointer", opacity: "0.5", top: "45px" }}
                        >
                          {showNewPassword ? <FaEye /> : <FaEyeSlash />}
                        </span>
                      </div>

                      <div className="mb-4 position-relative">
                        <label className="form-label" style={{ fontSize: "14px" }}>
                          Confirm New Password <span style={{ color: "red" }}>*</span>
                        </label>
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className="form-control"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        <span
                          className="position-absolute end-0 translate-middle-y me-3"
                          onClick={toggleConfirmPasswordVisibility}
                          style={{ cursor: "pointer", opacity: "0.5", top: "45px" }}
                        >
                          {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                        </span>

                        {confirmPassword &&
                          !newPassword.startsWith(confirmPassword) && (
                            <div className="text-danger mt-1" style={{ fontSize: "13px" }}>
                              Passwords do not match
                            </div>
                          )}
                      </div>

                      <button
                        type="submit"
                        className="btn w-100 fw-bold"
                        style={{
                          backgroundColor: "#0A4595",
                          color: "#fff",
                          padding: "10px 0",
                          borderRadius: "8px",
                          fontSize: "16px",
                        }}
                      >
                        Change Password
                      </button>
                    </form>
                  </>
                )}

                {activeTab === "settings" && (
                  <div>
                    <h5 className="fw-bold mb-2">Account Settings</h5>
                    <p className="text-start" style={{ fontSize: "14px", color: "#666" }}>
                      Update your details to keep your account current and secure.
                    </p>

                    <div className="d-flex flex-column gap-3 mt-1">
                      <div>
                        <label className="text-muted small">Name</label>
                        <input
                          className="form-control"
                          name="name"
                          value={editedUser.name}
                          placeholder={user.name}
                          onChange={handleChange}
                        />
                      </div>

                      <div>
                        <label className="text-muted small">Email</label>
                        <input
                          className="form-control"
                          value={user.email}
                          placeholder={user.email}
                          disabled
                        />
                      </div>

                      <div>
                        <label className="text-muted small">Mobile Number</label>
                        <input
                          className="form-control"
                          name="mobile"
                          value={editedUser.mobile}
                          placeholder={user.mobile_number}
                          onChange={handleChange}
                          maxLength={10}
                        />
                      </div>

                      <div>
                        <label className="text-muted small">Role</label>
                        <input
                          className="form-control"
                          value={userRole}
                          placeholder={userRole}
                          disabled
                        />
                      </div>
                    </div>

                    <div className="mt-4 d-flex flex-column flex-sm-row gap-3">
                      <button className="btn btn-primary" onClick={handleSave}>Save</button>
                      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("profile")}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>



        </div>
      </div>
    </div>





  );
};

export default Profile;
